import { SodiumPlus, X25519PublicKey } from 'sodium-plus';
import { isLocalDev } from '@/util/isLocalDev.ts';
import { useEventConfig } from '@/eventConfig/useEventConfig.ts';

/**
 * This implements libsodiums sealed box, a one-way encryption
 * where only the recipient can decrypt the message in the future
 *
 * @see https://stackoverflow.com/questions/54201183/how-to-perform-rsa-encrytion-with-ecb-mode-using-javascript-in-react-native
 * @uses https://github.com/paragonie/sodium-plus
 */

export const _encrypt = async (data: Record<any, any>, key?: string) => {
  if (!key) {
    throw new Error('Public key not found for encrypting data');
  }
  const sodium = await SodiumPlus.auto();
  // load the recipient's public key from a hex dump
  const publicKey = new X25519PublicKey(Buffer.from(key, 'hex'));

  // convert our data into JSON
  const json = JSON.stringify(data);

  // convert our UTF-8 JSON string to base64 first, else sodium would
  // later squeeze it into bytes (8bit aka. latin-1) :-/
  // @see https://github.com/paragonie/sodium-plus/blob/master/lib/util.js Util.toBuffer()
  const payload = Buffer.from(json).toString('base64');

  // finally encrypt the base64 encoded JSON data with the recipient's key
  const cipherText = await sodium.crypto_box_seal(payload, publicKey);

  // return a base64 encoded cipherText, everything else is not safe for submission over the line
  return Buffer.from(cipherText).toString('base64');
};

export const useEncryptPayload = () => {
  const { eventConfig } = useEventConfig();
  const publicKeyHex = eventConfig?.meta.publicKey || undefined;
  const key = isLocalDev() ? 'f82280fe1603d4944f1a6fdd1b93bdd41956b97718cf3a2f51b23a453497c02f' : publicKeyHex;
  const encrypt = (data: Record<any, any>) => _encrypt(data, key);
  return { encrypt };
};
